// src/components/ArticleList.tsx

import Image from "next/image";
import Link from "next/link";
import { Article } from "@/types/common";
import SectionHeading from "@/components/SectionHeading/SectionHeading";
import GumletImage from "@/utils/GumletSrcSet";

interface ArticleListProps {
  articles: Article[];
}

const ArticleList = ({ articles }: ArticleListProps) => {
  return (
    <div className="space-y-6 w-full lg:w-3/4">
      <SectionHeading
        title="CONTRIBUTIONS"
        textColor="text-white"
        bgColor="bg-[#121212]"
      />
      {!articles?.length ? (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="text-center">
            <h3 className="text-xl font-medium text-inkBlack mb-2">
              No Articles Found
            </h3>
            <p className="text-inactiveGray">
              There are currently no articles available.
            </p>
          </div>
        </div>
      ) : (
        <>
          {articles?.map((article, index) => (
            <div
              key={`${article.date}-${index}`}
              className="overflow-hidden group items-start gap-x-3 lg:gap-x-7 flex flex-row justify-start border-b-0.5 border-border last:border-b-0 w-full lg:py-0 py-4"
            >
              <div className="relative">
                <div className="lg:h-32 lg:w-48 h-24 w-32 flex-shrink-0">
                  <Link
                    prefetch={false}
                    href={article.link}
                    target="_blank"
                    className="relative"
                  >
                    <GumletImage
                      imageUrl={article?.image || "/assets/images/dummy.jpg"}
                      fallbackSrc="/assets/images/dummy.jpg"
                      alt={article.title}
                      width={192}
                      height={108}
                      className="object-cover rounded aspect-video"
                      loading="lazy"
                    />
                  </Link>
                </div>
              </div>
              <div className="w-full flex-1 min-w-0">
                <div className="flex items-center text-xs md:text-sm text-description font-normal md:mb-3 mb-1 min-w-0 ">
                  {article.categorySlug && (
                    <span className="text-inkBlack text-xs font-medium font-inter truncate min-w-0">
                      <Link
                        href={`/category/${article.categorySlug}`}
                        className="hover:underline"
                        prefetch={false}
                      >
                        {article.category}
                      </Link>
                    </span>
                  )}
                  {article.authorSlug && article.categorySlug && (
                    <span className="mx-2">/</span>
                  )}
                  {article.authorSlug && (
                    <span className="text-inactiveGray font-medium text-xs font-inter line-clamp-1">
                      <Link
                        href={`/author/${article.authorSlug}`}
                        className="hover:underline"
                        prefetch={false}
                      >
                        {article.author}
                      </Link>
                    </span>
                  )}
                </div>
                <Link href={article.link} target="_blank" prefetch={false}>
                  <h3 className="font-normal text-lg lg:text-xl text-inkBlack flex-1 line-clamp-4 leading-5">
                    {article.title}
                  </h3>
                </Link>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default ArticleList;
